import Experience from "@folio-2026/experience"
import { EVENTS, scaleValue } from "@folio-2026/shared"
import { type TextScene } from "@folio-2026/text"
import { uniform } from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

import Timeline from "./Timeline"
import { edgeFade } from "./utils/timelineShader"

// Default fade margin values
const DESKTOP_FADE_START_MARGIN = 0.15
const DESKTOP_FADE_END_MARGIN = 0.5
const MOBILE_FADE_START_MARGIN = 0
const MOBILE_FADE_END_MARGIN = 0.1
const MOBILE_BREAKPOINT = 768

const textSettings = {
  fadeStartMargin: uniform(0.15),
  fadeEndMargin: uniform(0.5),
  overallOpacity: uniform(1),
}
export default class AboutLeaf {
  experience: Experience
  sizes: Experience["sizes"]
  textScene!: TextScene
  timeline!: Timeline
  scrollWidth = 0
  debugFolder?: FolderApi

  constructor({
    textScene,
    debugFolder,
  }: {
    textScene: TextScene
    debugFolder?: FolderApi
  }) {
    this.experience = Experience.getInstance()
    this.sizes = this.experience.sizes

    this.textScene = textScene

    this.debugFolder = debugFolder

    this.experience.on(EVENTS.About.Entered, this.addAboutPage.bind(this))
    this.experience.on(EVENTS.About.Exiting, this.removeAboutPage.bind(this))

    this.experience.on(
      EVENTS.About.Scroll,
      this.manageHorizontalScroll.bind(this),
    )

    this.updateFadeMargins()

    this.setDebug()

    // Emit ready event asynchronously
    queueMicrotask(() => {
      this.experience.trigger(EVENTS.About.LeafReady)
    })
  }

  private updateFadeMargins() {
    const isMobile = this.sizes.width < MOBILE_BREAKPOINT
    textSettings.fadeStartMargin.value = isMobile
      ? MOBILE_FADE_START_MARGIN
      : DESKTOP_FADE_START_MARGIN
    textSettings.fadeEndMargin.value = isMobile
      ? MOBILE_FADE_END_MARGIN
      : DESKTOP_FADE_END_MARGIN
    // Update text materials to apply the new fade margins
    this.updateTextMaterials()
  }

  /**
   * Manage the horizontal scroll of the about page.
   */
  manageHorizontalScroll(e: unknown) {
    const { progress, target } = e as { progress: number; target: HTMLElement }

    // Scroll in reverse
    const p = progress

    if (!this.scrollWidth) {
      this.scrollWidth = target.scrollWidth
    }
    const scrollTo = this.scrollWidth - this.sizes.width
    const x = scaleValue(p, [0, 1], [0, scrollTo])

    this.textScene.texts.forEach((text) => {
      if (text.name === "about") {
        const boundLeft = text.bounds.left
        const halfScreenWidth = this.sizes.width / 2

        if (text.mesh) {
          const positionX = boundLeft - halfScreenWidth + x
          const positionY =
            -text.y +
            this.experience.scrollInfo.animatedScroll +
            this.experience.sizes.height / 2 -
            text.bounds.height

          text.mesh.position.x = positionX
          text.mesh.position.y = positionY

          if (text.mesh.material) {
            const material = text.mesh.material as THREE.NodeMaterial
            const opacityNode = material.opacityNode as THREE.Node

            material.opacityNode = edgeFade(
              opacityNode,
              textSettings.fadeStartMargin,
              textSettings.fadeEndMargin,
              textSettings.overallOpacity,
            )
          }
        }
      }
    })
  }

  positionFixedTexts() {
    this.textScene.texts.forEach((text) => {
      if (
        text.name === "about-timeline-caption" ||
        text.name === "about-text"
      ) {
        const positionX = text.bounds.left - this.sizes.width / 2
        const positionY =
          -text.y + this.experience.sizes.height / 2 - text.bounds.height

        text.mesh.position.x = positionX
        text.mesh.position.y = positionY
      }
    })
  }

  /**
   * Add all the objects needed for the about page.
   */
  addAboutPage(e: unknown) {
    const { target } = e as { target: HTMLElement }

    this.textScene.createSmallButtons("about")
    this.textScene.createTexts("about") // Timeline
    this.textScene.createTexts("about-text") // Intro text
    this.textScene.createTexts("about-timeline-caption") // Caption

    this.positionFixedTexts()

    this.textScene.texts.forEach((text) => {
      if (
        text.name === "about-timeline-caption" ||
        text.name === "about-text"
      ) {
        text.reveal()
      }
    })

    const perlin = this.experience.globalResources["perlin"] as THREE.Texture
    perlin.wrapS = THREE.RepeatWrapping
    perlin.wrapT = THREE.RepeatWrapping

    this.timeline = new Timeline({
      scene: this.textScene.scene,
      noise: perlin,
      debugFolder: this.debugFolder,
    })

    this.manageHorizontalScroll({
      progress: 0,
      target: target,
    })

    // Hide intro texts
    const introTexts = this.textScene.texts.filter(
      (text) => text.name === "loader",
    )
    introTexts.forEach((text) => text.leave())

    // Reveal about texts
    const aboutTexts = this.textScene.texts.filter(
      (text) => text.name === "about",
    )

    aboutTexts.forEach((text) => text.reveal())
  }

  /**
   * Remove all the objects from the about page. (geometries, materials, meshes, etc.)
   */
  async removeAboutPage() {
    const aboutTexts = this.textScene.texts.filter(
      (text) =>
        text.name === "about" ||
        text.name === "about-text" ||
        text.name === "about-timeline-caption",
    )

    await Promise.all([
      this.timeline.leave(),
      ...aboutTexts.map((text) => text.leave()),
    ])

    aboutTexts.forEach((text) => text.remove())

    this.textScene.texts = this.textScene.texts.filter(
      (text) =>
        text.name !== "about" &&
        text.name !== "about-text" &&
        text.name !== "about-timeline-caption",
    )
  }

  resize() {
    this.scrollWidth = 0

    // Update fade margins based on screen size
    this.updateFadeMargins()

    // Resize all texts (this will recalculate their bounds)
    this.textScene.texts.forEach((text) => {
      if (
        text.name === "about" ||
        text.name === "about-text" ||
        text.name === "about-timeline-caption"
      ) {
        text.resize()
      }
    })

    // Update timeline positions (needs to happen after text resize to get correct bounds)
    if (this.timeline) {
      this.timeline.resize()
    }

    // Update fixed texts positions after resize
    this.positionFixedTexts()
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "⌛ Timeline Text",
      expanded: false,
    })

    const textOpacitySettings = {
      fadeStartMargin: textSettings.fadeStartMargin.value,
      fadeEndMargin: textSettings.fadeEndMargin.value,
      overallOpacity: textSettings.overallOpacity.value,
    }

    folder
      .addBinding(textOpacitySettings, "fadeStartMargin", {
        label: "Fade Start Margin",
        min: 0,
        max: 0.5,
        step: 0.01,
      })
      .on("change", (ev) => {
        textSettings.fadeStartMargin.value = ev.value
        // Update all about text materials
        this.updateTextMaterials()
      })

    folder
      .addBinding(textOpacitySettings, "fadeEndMargin", {
        label: "Fade End Margin",
        min: 0,
        max: 0.5,
        step: 0.01,
      })
      .on("change", (ev) => {
        textSettings.fadeEndMargin.value = ev.value
        // Update all about text materials
        this.updateTextMaterials()
      })

    folder
      .addBinding(textOpacitySettings, "overallOpacity", {
        label: "Overall Opacity",
        min: 0,
        max: 1,
        step: 0.01,
      })
      .on("change", (ev) => {
        textSettings.overallOpacity.value = ev.value
        // Update all about text materials
        this.updateTextMaterials()
      })
  }

  private updateTextMaterials() {
    this.textScene.texts.forEach((text) => {
      if (text.name === "about" && text.mesh?.material) {
        const material = text.mesh.material as THREE.NodeMaterial
        const opacityNode = material.opacityNode as THREE.Node

        material.opacityNode = edgeFade(
          opacityNode,
          textSettings.fadeStartMargin,
          textSettings.fadeEndMargin,
          textSettings.overallOpacity,
        )
      }
    })
  }
}
